import { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { styles } from "../styles";
import { displayName, hasCapability } from "../utils/capabilities";
import { createContract } from "../models/models";
import BackButton from "../components/BackButton";
import EquipmentMultiSelect from "../components/EquipmentMultiSelect";
import { projectedAnnualPrices } from "../utils/contracts";

const money = (n) =>
  Number(n || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });

const STATUS_LABEL = {
  active: "Active",
  expired: "Expired",
  cancelled: "Cancelled",
};

const STATUS_COLOR = {
  active: { bg: "#c9efc9", fg: "#1a5f1f" },
  expired: { bg: "#e0e9f7", fg: "#1a5fb4" },
  cancelled: { bg: "#f8d7da", fg: "#7a1f1f" },
};

const VISITS_PER_YEAR = {
  monthly: 12,
  quarterly: 4,
  biannual: 2,
  annual: 1,
};

/**
 * Contractor-side maintenance contracts list. Per project_maintenance_contracts
 * spec Phase 1: contract creation spawns N visit jobs at creation time with
 * status "scheduled" so they don't clutter the active JobsPage tiles. Visits
 * become real "unassigned" jobs when admin activates them from the
 * ContractDetailPage (which lists the full timeline).
 */
export default function ContractsPage({
  contractsRaw = [],
  customers = [],
  actions,
  role,
  capabilities,
  orgId,
  onOpenContract,
  onBack,
}) {
  const canCreate = hasCapability(role, capabilities, "editJobs");

  // Org users — feeds the Default-tech picker on the create form.
  // Same listener shape JobDetailPage uses (managers/admins can read;
  // techs can't, so only fetch when we can create contracts).
  const [orgUsers, setOrgUsers] = useState([]);
  useEffect(() => {
    if (!orgId || !canCreate) return;
    const q = query(collection(db, "users"), where("orgId", "==", orgId));
    const unsub = onSnapshot(q, (snap) =>
      setOrgUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
    return unsub;
  }, [orgId, canCreate]);
  const fieldTechs = orgUsers.filter(
    (u) => !u.deleted && u.fieldEligible !== false
  );
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showCreate, setShowCreate] = useState(false);

  // --- New Contract form state ---
  const [title, setTitle] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [locationId, setLocationId] = useState("");
  const [equipmentIds, setEquipmentIds] = useState([]);
  const [startDate, setStartDate] = useState(
    () => new Date().toISOString().slice(0, 10)
  );
  const [termYears, setTermYears] = useState(1);
  const [annualPrice, setAnnualPrice] = useState("");
  const [annualEscalationPct, setAnnualEscalationPct] = useState("");
  const [defaultTechId, setDefaultTechId] = useState("");
  const [billingMode, setBillingMode] = useState("per-visit");
  const [billingInterval, setBillingInterval] = useState("quarterly");
  const [visitInterval, setVisitInterval] = useState("quarterly");
  const [scopeOfWork, setScopeOfWork] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState("");

  const customerObj = customers.find((c) => c.id === customerId);
  const locationObj = customerObj?.locations?.find(
    (l) => l.id === locationId
  );
  const availableEquipment = locationObj?.equipment || [];

  const totalVisits =
    (VISITS_PER_YEAR[visitInterval] || 4) * (termYears || 1);
  const pricePerVisit =
    totalVisits > 0 ? Number(annualPrice || 0) / VISITS_PER_YEAR[visitInterval] : 0;

  const resetForm = () => {
    setTitle("");
    setCustomerId("");
    setLocationId("");
    setEquipmentIds([]);
    setStartDate(new Date().toISOString().slice(0, 10));
    setTermYears(1);
    setAnnualPrice("");
    setAnnualEscalationPct("");
    setDefaultTechId("");
    setBillingMode("per-visit");
    setBillingInterval("quarterly");
    setVisitInterval("quarterly");
    setScopeOfWork("");
    setMsg("");
  };

  const handleSubmit = async () => {
    if (!customerId || !locationId || !startDate || !annualPrice) {
      setMsg("Customer, building, start date, and annual price are required.");
      return;
    }
    setSubmitting(true);
    setMsg("");
    try {
      const payload = {
        ...createContract(title || "PM Contract"),
        title: title.trim() || "PM Contract",
        customerId,
        locationId,
        equipmentIds,
        startDate,
        termYears: Number(termYears),
        annualPrice: Number(annualPrice),
        annualEscalationPct: Number(annualEscalationPct) || 0,
        defaultTechId: defaultTechId || "",
        billingMode,
        billingInterval,
        visitInterval,
        scopeOfWork,
      };
      await actions.addContract(payload);
      resetForm();
      setShowCreate(false);
    } catch (err) {
      setMsg(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Apply filters + sort newest first.
  const contracts = useMemo(() => {
    const term = search.trim().toLowerCase();
    return contractsRaw
      .filter((c) => !statusFilter || c.status === statusFilter)
      .filter((c) => {
        if (!term) return true;
        const customer = customers.find((cc) => cc.id === c.customerId);
        const location = customer?.locations?.find(
          (l) => l.id === c.locationId
        );
        const hay = [
          c.contractNumber,
          c.title,
          c.scopeOfWork,
          customer?.name,
          location?.name,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(term);
      })
      .sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""));
  }, [contractsRaw, customers, search, statusFilter]);

  // Rough rollup numbers for the header.
  const activeCount = contractsRaw.filter(
    (c) => c.status === "active"
  ).length;
  const annualRevenue = contractsRaw
    .filter((c) => c.status === "active")
    .reduce((s, c) => s + Number(c.annualPrice || 0), 0);

  return (
    <>
      <BackButton onClick={onBack} />
      <h2 style={{ margin: "8px 0 4px" }}>Maintenance Contracts</h2>
      <div style={{ fontSize: "13px", color: "#666", marginBottom: "12px" }}>
        {activeCount} active · Annual recurring: {money(annualRevenue)}
      </div>

      {canCreate && (
        <button
          style={styles.button}
          onClick={() => setShowCreate((s) => !s)}
        >
          {showCreate ? "Cancel" : "+ New Contract"}
        </button>
      )}

      {showCreate && (
        <div style={{ ...styles.card, maxWidth: "780px", marginTop: "8px" }}>
          <h3 style={{ marginTop: 0 }}>New Maintenance Contract</h3>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: "10px",
            }}
          >
            <div>
              <label style={{ display: "block", fontSize: "12px", color: "#666" }}>
                Title
              </label>
              <input
                style={{ ...styles.input, margin: 0, width: "100%" }}
                placeholder="e.g. Quarterly PM — Acme HQ"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <div>
              <label style={{ display: "block", fontSize: "12px", color: "#666" }}>
                Customer
              </label>
              <select
                style={{ ...styles.input, margin: 0, width: "100%" }}
                value={customerId}
                onChange={(e) => {
                  setCustomerId(e.target.value);
                  setLocationId("");
                  setEquipmentIds([]);
                }}
              >
                <option value="">— pick customer —</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: "block", fontSize: "12px", color: "#666" }}>
                Building
              </label>
              <select
                style={{ ...styles.input, margin: 0, width: "100%" }}
                value={locationId}
                onChange={(e) => {
                  setLocationId(e.target.value);
                  setEquipmentIds([]);
                }}
                disabled={!customerObj}
              >
                <option value="">— pick building —</option>
                {(customerObj?.locations || []).map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: "block", fontSize: "12px", color: "#666" }}>
                Start date{" "}
                <span style={{ color: "#888", fontSize: "11px" }}>
                  (backdate OK for existing contracts)
                </span>
              </label>
              <input
                type="date"
                style={{ ...styles.input, margin: 0, width: "100%" }}
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div>
              <label style={{ display: "block", fontSize: "12px", color: "#666" }}>
                Term (years)
              </label>
              <select
                style={{ ...styles.input, margin: 0, width: "100%" }}
                value={termYears}
                onChange={(e) => setTermYears(Number(e.target.value))}
              >
                <option value={1}>1 year</option>
                <option value={2}>2 years</option>
                <option value={3}>3 years</option>
                <option value={5}>5 years</option>
              </select>
            </div>
            <div>
              <label style={{ display: "block", fontSize: "12px", color: "#666" }}>
                Annual price ($)
              </label>
              <input
                type="number"
                style={{ ...styles.input, margin: 0, width: "100%" }}
                placeholder="e.g. 12000"
                value={annualPrice}
                onChange={(e) => setAnnualPrice(e.target.value)}
              />
            </div>
            <div>
              <label style={{ display: "block", fontSize: "12px", color: "#666" }}>
                Annual escalator (%)
              </label>
              <input
                type="number"
                step="0.1"
                style={{ ...styles.input, margin: 0, width: "100%" }}
                placeholder="0 = flat. Industry standard 3–5%."
                value={annualEscalationPct}
                onChange={(e) => setAnnualEscalationPct(e.target.value)}
                title="Year-N price = year-1 × (1 + pct/100)^(N-1). Leave 0 for flat pricing across the full term."
              />
            </div>
            <div>
              <label style={{ display: "block", fontSize: "12px", color: "#666" }}>
                Default tech
              </label>
              <select
                style={{ ...styles.input, margin: 0, width: "100%" }}
                value={defaultTechId}
                onChange={(e) => setDefaultTechId(e.target.value)}
                title="Pre-assigned to every visit spawned by this contract. Leave blank to let dispatch assign per visit. Office staff are filtered out."
              >
                <option value="">— None (dispatch assigns per visit) —</option>
                {fieldTechs.map((u) => (
                  <option key={u.id} value={u.id}>
                    {displayName(u.profile, u.email)} ({u.role})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: "block", fontSize: "12px", color: "#666" }}>
                Visit interval
              </label>
              <select
                style={{ ...styles.input, margin: 0, width: "100%" }}
                value={visitInterval}
                onChange={(e) => setVisitInterval(e.target.value)}
              >
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="biannual">Bi-annual</option>
                <option value="annual">Annual</option>
              </select>
            </div>
            <div>
              <label style={{ display: "block", fontSize: "12px", color: "#666" }}>
                Billing mode
              </label>
              <select
                style={{ ...styles.input, margin: 0, width: "100%" }}
                value={billingMode}
                onChange={(e) => setBillingMode(e.target.value)}
              >
                <option value="per-visit">
                  Per visit (bill when each visit completes)
                </option>
                <option value="per-interval">
                  Per interval (bill on calendar regardless of visits)
                </option>
              </select>
            </div>
            {billingMode === "per-interval" && (
              <div>
                <label
                  style={{ display: "block", fontSize: "12px", color: "#666" }}
                >
                  Billing interval
                </label>
                <select
                  style={{ ...styles.input, margin: 0, width: "100%" }}
                  value={billingInterval}
                  onChange={(e) => setBillingInterval(e.target.value)}
                >
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="biannual">Bi-annual</option>
                  <option value="annual">Annual</option>
                </select>
              </div>
            )}
          </div>

          {availableEquipment.length > 0 && (
            <div style={{ marginTop: "12px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "12px",
                  color: "#666",
                  marginBottom: "4px",
                }}
              >
                Covered equipment under contract
              </label>
              <EquipmentMultiSelect
                equipment={availableEquipment}
                selectedIds={equipmentIds}
                onChange={setEquipmentIds}
              />
            </div>
          )}

          <div style={{ marginTop: "12px" }}>
            <label style={{ display: "block", fontSize: "12px", color: "#666" }}>
              Scope of work (one item per line — renders as bullets)
            </label>
            <textarea
              style={styles.textarea}
              rows={3}
              placeholder="e.g.&#10;Quarterly filter change&#10;Belt + bearing inspection&#10;Refrigerant level check"
              value={scopeOfWork}
              onChange={(e) => setScopeOfWork(e.target.value)}
            />
          </div>

          <div
            style={{
              marginTop: "10px",
              padding: "8px 10px",
              background: "#f3f8ff",
              border: "1px solid #d1e2f8",
              borderRadius: "4px",
              fontSize: "12px",
              color: "#1a5fb4",
            }}
          >
            <strong>Preview:</strong> Spawns {totalVisits} visit
            {totalVisits === 1 ? "" : "s"} ({VISITS_PER_YEAR[visitInterval]}/yr ×{" "}
            {termYears} yr).{" "}
            {Number(annualEscalationPct) > 0 ? (
              <>Year-1 per-visit ≈ {money(pricePerVisit)}.</>
            ) : (
              <>Per-visit price ≈ {money(pricePerVisit)}. Lifetime contract value ≈{" "}
              {money(Number(annualPrice || 0) * termYears)}.</>
            )}
            {Number(annualEscalationPct) > 0 && (() => {
              const projection = projectedAnnualPrices({
                annualPrice: Number(annualPrice) || 0,
                annualEscalationPct: Number(annualEscalationPct) || 0,
                termYears: Number(termYears) || 1,
                visitInterval,
              });
              const lifetime = projection.reduce((s, y) => s + y.annualPrice, 0);
              return (
                <div style={{ marginTop: "6px" }}>
                  <strong>With {Number(annualEscalationPct)}% escalator:</strong>
                  <div
                    style={{
                      marginTop: "2px",
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                      gap: "2px 12px",
                    }}
                  >
                    {projection.map((y) => (
                      <div key={y.year}>
                        Year {y.year}: <strong>{money(y.annualPrice)}</strong>{" "}
                        ({money(y.perVisitPrice)}/visit)
                      </div>
                    ))}
                  </div>
                  <div style={{ marginTop: "4px" }}>
                    Lifetime contract value ≈ <strong>{money(lifetime)}</strong>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Backdate guidance — when admin migrates an existing PM agreement
              from another platform, start date may be months/years in the
              past. All visits still spawn as "scheduled"; admin marks
              already-done ones complete + already-skipped ones skipped
              via the contract's timeline page. */}
          {startDate &&
            startDate < new Date().toISOString().slice(0, 10) && (
              <div
                style={{
                  marginTop: "8px",
                  padding: "8px 10px",
                  background: "#fff8e0",
                  border: "1px solid #f0d27a",
                  borderRadius: "4px",
                  fontSize: "12px",
                  color: "#6a4900",
                }}
              >
                <strong>Backdated contract.</strong> Start date is in the
                past — looks like you're migrating an existing agreement.
                All {totalVisits} visits will spawn as "Scheduled". After
                creation, open the contract and use Activate / Skip /
                Reschedule on each past-date visit to backfill what
                actually happened.
              </div>
            )}

          {msg && (
            <div
              style={{
                marginTop: "8px",
                color: "#c33",
                fontSize: "12px",
              }}
            >
              {msg}
            </div>
          )}

          <div style={{ display: "flex", gap: "8px", marginTop: "12px" }}>
            <button
              style={{ ...styles.primaryButton, margin: 0 }}
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? "Creating contract…" : "Create Contract"}
            </button>
            <button
              style={{ ...styles.button, margin: 0 }}
              onClick={() => {
                resetForm();
                setShowCreate(false);
              }}
              disabled={submitting}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div
        style={{
          display: "flex",
          gap: "8px",
          flexWrap: "wrap",
          margin: "12px 0",
        }}
      >
        <input
          type="text"
          placeholder="Search by contract # / title / customer / building..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ ...styles.input, minWidth: "240px", flex: 1 }}
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="expired">Expired</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {contracts.length === 0 ? (
        <div
          style={{
            padding: "16px",
            color: "#888",
            border: "1px dashed #ddd",
            borderRadius: "6px",
          }}
        >
          {contractsRaw.length === 0
            ? "No contracts yet. Click + New Contract above to set up your first PM agreement."
            : "No contracts match the current filters."}
        </div>
      ) : (
        <div style={styles.card}>
          {contracts.map((c) => {
            const customer = customers.find((cc) => cc.id === c.customerId);
            const location = customer?.locations?.find(
              (l) => l.id === c.locationId
            );
            const status = STATUS_COLOR[c.status] || STATUS_COLOR.active;
            return (
              <div
                key={c.id}
                style={{
                  borderTop: "1px solid #eee",
                  padding: "10px 0",
                  display: "flex",
                  gap: "10px",
                  alignItems: "center",
                  flexWrap: "wrap",
                  cursor: "pointer",
                }}
                onClick={() => onOpenContract && onOpenContract(c)}
              >
                <div style={{ flex: 1, minWidth: "260px" }}>
                  <div style={{ fontSize: "14px", fontWeight: 600 }}>
                    {c.contractNumber || "(no #)"}
                    {c.title ? ` — ${c.title}` : ""}
                    <span
                      style={{
                        marginLeft: "8px",
                        fontSize: "10px",
                        padding: "1px 6px",
                        borderRadius: "3px",
                        textTransform: "uppercase",
                        background: status.bg,
                        color: status.fg,
                      }}
                    >
                      {STATUS_LABEL[c.status] || c.status}
                    </span>
                  </div>
                  <div style={{ fontSize: "12px", color: "#666", marginTop: "2px" }}>
                    {customer?.name || "—"} · {location?.name || "—"}
                  </div>
                  <div style={{ fontSize: "12px", color: "#888", marginTop: "2px" }}>
                    {c.startDate ? `${c.startDate} → ${c.endDate || "?"}` : ""}{" "}
                    · {c.visitInterval}{" "}
                    visits · {c.billingMode}
                  </div>
                </div>
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: 700,
                    color: "#1a5fb4",
                    minWidth: "90px",
                    textAlign: "right",
                  }}
                >
                  {money(c.annualPrice)}/yr
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
